import { useEffect, useRef, useState } from "react";

type BreadcrumbsProps = {
  breadcrumbs: string[];
  onNavigatePath: (path: string | null) => void;
  className?: string;
};

export function Breadcrumbs({
  breadcrumbs,
  onNavigatePath,
  className = "",
}: BreadcrumbsProps) {
  const crumbs = breadcrumbs.length ? breadcrumbs : ["root"];
  const MAX_VISIBLE_CRUMBS = 4;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [forceCollapse, setForceCollapse] = useState(false);

  type RenderedCrumb = {
    label: string;
    index?: number;
    isEllipsis?: boolean;
    hiddenLabels?: string[];
  };

  const shouldCollapseByCount = crumbs.length > MAX_VISIBLE_CRUMBS;
  const shouldCollapse = shouldCollapseByCount || forceCollapse;

  useEffect(() => {
    setForceCollapse(false);
  }, [breadcrumbs.join("/")]);

  useEffect(() => {
    if (forceCollapse) return;
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const update = () => {
      if (el.scrollWidth > el.clientWidth) {
        setForceCollapse(true);
      }
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [forceCollapse, breadcrumbs.join("/")]);

  const buildRenderedCrumbs = (): RenderedCrumb[] => {
    if (!shouldCollapse || crumbs.length <= 2) {
      return crumbs.map((label, index) => ({ label, index }));
    }

    const tailCount = Math.min(2, Math.max(1, crumbs.length - 2));
    const tailCrumbs = crumbs.slice(-tailCount);
    return [
      { label: crumbs[0], index: 0 },
      {
        label: "...",
        isEllipsis: true,
        hiddenLabels: crumbs.slice(1, -tailCount),
      },
      ...tailCrumbs.map((label, idx) => ({
        label,
        index: crumbs.length - tailCount + idx,
      })),
    ];
  };

  const renderedCrumbs = buildRenderedCrumbs();

  return (
    <div
      ref={containerRef}
      className={`${className} flex flex-nowrap items-center gap-1 overflow-hidden text-xs text-gray-600`}
    >
      <span className="text-gray-500">:/</span>
      {renderedCrumbs.map((crumb, idx) => {
        const targetPath = !crumb.isEllipsis
          ? breadcrumbs.slice(0, (crumb.index ?? 0) + 1).join("/")
          : "";
        const navigateTarget =
          breadcrumbs.length && !crumb.isEllipsis ? targetPath : null;
        const isLast = idx === renderedCrumbs.length - 1;
        return (
          <span
            key={`${crumb.label}-${idx}`}
            className="flex min-w-0 items-center gap-1"
          >
            {crumb.isEllipsis ? (
              <span
                title={crumb.hiddenLabels?.join(" / ")}
                className="text-gray-400"
              >
                ...
              </span>
            ) : (
              <button
                className={`underline-offset-2 ${isLast ? "font-semibold" : "text-pinkCustom hover:cursor-pointer"} max-w-[180px] truncate text-left`}
                onClick={() => onNavigatePath(navigateTarget)}
              >
                {crumb.label}
              </button>
            )}
            {!isLast ? <span className="text-gray-400">/</span> : null}
          </span>
        );
      })}
    </div>
  );
}
